import {
  Home,
  MessageSquare,
  Package,
  Users,
  UserCog,
  BellRing,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react';

/**
 * 工厂端导航（G4）。**跟内部那份 `modules/registry.ts` 是两份东西，不要合并。**
 *
 * 内部 admin 有 9 个模块组六十多个页面，工厂端要三四个入口 ——
 * 这是侧边栏里有什么的差别，不是颜色字体的差别。合成一份再靠标记过滤，
 * 就变成「默认可见、逐项排除」，每新增一个内部页面就多一次「租户能不能看见」的判断，
 * 漏一次就是泄漏。跟 P1 立的原则相反。
 */

export interface PortalNavItem {
  id: string;
  label: string;
  /**
   * 手机底部标签栏用的短名。不写就用 `label`。
   *
   * 底栏一行要塞七个格子,390 宽下每格 55px —— 「我的 WhatsApp」在那儿会折成
   * 两行,把整条标签栏顶高一截,而且只有它一个是两行,看起来像坏了。
   * 侧栏横向有的是地方,那边仍然用全名。
   */
  shortLabel?: string;
  to: string;
  icon: LucideIcon;
  /**
   * 画这一项需要哪个能力（G5 的能力矩阵，可配置的那三个）。不写 = 不受能力表管。
   *
   * 注意这**不是安全边界** —— 数据本身由 G3 的出口闸和每条路由自己保证，
   * 免费档就算把地址敲进去也拿不到真实身份。这里过滤只是不画一个点进去没内容的入口。
   */
  requires?: string;
  /**
   * 只有付费档才有，**且不可配置**。
   *
   * 客户池（看真实身份）刻意不做成能力开关：配错的后果不可逆 ——
   * 人一旦看到客户的真实邮箱电话，撤不回来。跟 G3 的脱敏同等对待，硬接在档位上。
   */
  paidOnly?: boolean;
}

export const PORTAL_NAV: PortalNavItem[] = [
  { id: 'home', label: '待办', to: '/portal', icon: Home },
  { id: 'inquiries', label: '询盘', to: '/portal/inquiries', icon: MessageSquare },
  { id: 'orders', label: '订单', to: '/portal/orders', icon: Package },
  {
    id: 'customers',
    label: '客户',
    to: '/portal/customers',
    icon: Users,
    paidOnly: true,
  },
  /**
   * 通知设置。**两档都有** —— 「有询盘了怎么知道」是付费版能不能用起来的开关，
   * 而免费联营侧要靠它才知道平台转了单过来。
   */
  { id: 'notify', label: '通知', to: '/portal/notify', icon: BellRing },
  /**
   * P3 WhatsApp 扫码托管。**只有付费档有，而且还要单独开 `wa_relay_enabled`** ——
   * 这里只按档位过滤（前端拿不到那个开关），真正的门在后端：
   * 没开通的话 `/wa-relay/*` 回 403 并说明原因，页面照实显示「还没为你开通」。
   *
   * 为什么入口对没开通的付费工厂也画出来：他知道有这个功能（销售会讲），
   * 画一个能点进去看到「联系业务开通」的入口，比什么都没有好 ——
   * 后者会让他以为我们没做。
   */
  {
    id: 'whatsapp',
    label: '我的 WhatsApp',
    shortLabel: 'WhatsApp',
    to: '/portal/whatsapp',
    icon: MessageCircle,
    paidOnly: true,
  },
  { id: 'account', label: '账号', to: '/portal/account', icon: UserCog },
];

/**
 * 底栏那个名字占多宽,按「几个汉字」算。
 *
 * 不能直接数字符数:`WhatsApp` 是 8 个字符但只有 4 个汉字宽,
 * 而「我的 WhatsApp」是 6.5 个汉字宽 —— 后者在 390 的屏上会折行,前者不会。
 * 390 宽下底栏七格,每格约 55px,15px 的字放得下 5 个汉字宽。
 */
export function navLabelWidth(label: string): number {
  let w = 0;
  for (const ch of label) {
    // CJK 和全角标点按 1 算,其余(拉丁字母、数字、空格)按 0.5
    w += /[\u2E80-\u9FFF\uFF00-\uFFEF]/.test(ch) ? 1 : 0.5;
  }
  return w;
}

/** 免费联营档：客户是我们的，工厂只看得到代号 */
export function isFreeTier(tier: string | undefined | null): boolean {
  // 读不到按最严的来 —— 跟后端 G3 同一个口径。
  // 空字符串也算「没读到」：`?? 'free'` 拦不住 ''，而后端返回空串是可能的
  return !tier || tier === 'free';
}

/**
 * 画哪几个入口。
 *
 * 优先读后端算好的能力表 —— 「给某一家单开一个功能」只改数据，前端不用跟着改。
 * 后端没给（老 session、老版本后端）时退回按档位判，口径跟以前一致：
 * 免费档没有客户池。退回时**按最严的来**，不是「读不到就都给」。
 */
export function visibleNav(
  tier: string | undefined | null,
  capabilities?: Record<string, boolean>,
): PortalNavItem[] {
  const free = isFreeTier(tier);
  return PORTAL_NAV.filter((n) => {
    // 不可配置的那条：硬接在档位上，能力表说什么都不算
    if (n.paidOnly && free) return false;
    if (!n.requires) return true;
    if (capabilities && n.requires in capabilities) return capabilities[n.requires] === true;
    return !free;
  });
}

/** 单点判一个能力，口径跟 visibleNav 一致 */
export function hasCapability(
  cap: string,
  tier: string | undefined | null,
  capabilities?: Record<string, boolean>,
): boolean {
  if (capabilities && cap in capabilities) return capabilities[cap] === true;
  return !isFreeTier(tier);
}
